import React, { useEffect, useState } from 'react';
import { Form } from '@unform/web';
import pt from 'date-fns/locale/pt';
import { toast } from 'react-toastify';
import { addHours, format, isValid, parse } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { FiEye, FiPlus, FiSearch } from 'react-icons/fi';

import Card from 'components/Card';
import Pagination from 'components/Pagination';
import Button from 'components/Button';
import Avatar from 'components/Avatar';
import Loader from 'components/ContentLoader/GenericLoader';
import NotFound from 'components/NotFound';
import Input from 'components/Input';
import Tooltip from 'components/Tooltip';

import { Usuario } from 'pages/@types';
import api from 'services/api';
import AppError from 'utils/AppError';
import NoSolicitacaoChamada from 'assets/Fill out-pana.svg';
import config from 'config/environment';

import { Container } from './styles';
import { Table } from 'styles/global';
export interface SolicitacaoDigital {
  id: number;
  nome: string;
  tarefa: string;
  produto: string;
  datafim: string;
  praca: string;
  telefone?: string;
  solicitante: Usuario;
}

export default function SolicitacaoDigital() {
  // react-router-dom
  const navigate = useNavigate();
  /**
   * USE STATE
   */
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filtro, setFiltro] = useState('');
  const [loading, setLoading] = useState(false);
  const [solicitacoes, setSolicitacoes] = useState<SolicitacaoDigital[]>([]);

  /**
   * USE EFFECT
   */
  useEffect(() => {
    const controller = new AbortController();
    async function loadSolicitacoes() {
      try {
        // Validação em caso de busca por data (usando > ou <)
        if (filtro && (filtro.startsWith('>') || filtro.startsWith('<'))) {
          const parsedDate = parse(
            filtro.replace('<', '').replace('>', ''),
            'P',
            new Date(),
            { locale: pt },
          );
          if (!isValid(parsedDate)) {
            toast.error('Data inválida');
            return;
          }
        }
        setLoading(true);
        const response = await api.get('/solicitacaodigital', {
          signal: controller.signal,
          params: {
            ...(filtro && { search: filtro }),
            page,
          },
        });
        if (response && response.data) {
          setSolicitacoes(response.data.solicitacoes);
          setPages(response.data.pages);
        }
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
      }
      setLoading(false);
    }
    loadSolicitacoes();
    return () => {
      controller.abort();
    };
  }, [filtro, page]);

  return (
    <Container>
      <Card>
        <header>
          <h4>LISTA DE SOLICITAÇÕES</h4>
          <Pagination
            total={pages}
            activePage={page}
            onClick={page => setPage(page)}
          />
          <Button
            color="primary"
            href={'/solicitacaodigital/novo'}
            tooltip="Criar nova Solicitação"
          >
            NOVA SOLICITAÇÃO
            <FiPlus />
          </Button>
          <div>
            <Form
              onSubmit={e => {
                setFiltro(e.searchLocal);
                setPage(1);
              }}
            >
              <Input
                id="filtro"
                name="searchLocal"
                placeholder="Buscar na Tabela"
                icon={<FiSearch />}
              />
              <Tooltip target="filtro" position="bottom">
                Buscas possíveis:
                <br />
                Pelo nome ou pela emissora.
                <br />
                Pela data fim usando os operadores &lt; e &gt;
                <br />
                Ex: &gt;01/01/2024 trará as solicitações que a data final sejam
                a partir de 02/01/2024
              </Tooltip>
            </Form>
          </div>
        </header>
        {loading ? (
          <Loader />
        ) : !!solicitacoes && solicitacoes.length ? (
          <div style={{ padding: `0 15px` }}>
            <Table>
              <thead>
                <tr>
                  <th>NOME</th>
                  <th style={{ width: 430, textAlign: 'center' }}>TAREFA</th>
                  <th style={{ width: 200, textAlign: 'center' }}>PRAÇA</th>
                  <th style={{ width: 200, textAlign: 'center' }}>PRODUTO</th>
                  <th style={{ width: 150, textAlign: 'center' }}>
                    DATA FINAL
                  </th>
                  <th style={{ width: 120, textAlign: 'center' }}>
                    SOLICITANTE
                  </th>
                  <th style={{ width: 120, textAlign: 'center' }}>
                    VISUALIZAR
                  </th>
                </tr>
              </thead>
              <tbody>
                {solicitacoes.map(s => (
                  <tr key={s.id}>
                    <td>{s.nome}</td>
                    <td style={{ textAlign: 'center' }}>{s.tarefa}</td>
                    <td style={{ textAlign: 'center' }}>{s.praca}</td>
                    <td style={{ textAlign: 'center' }}>{s.produto}</td>

                    <td style={{ textAlign: 'center' }}>
                      {format(addHours(new Date(s.datafim), 3), 'dd/MM/yyyy')}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <Avatar
                        size="40"
                        name={s.solicitante?.nome}
                        src={s.solicitante?.avatar_url}
                        round
                      />
                    </td>
                    <td style={{ display: 'flex', justifyContent: 'center' }}>
                      <Button
                        id={`id_visualizar_${s.id}`}
                        style={{ textAlign: 'center' }}
                        fill
                        color="font"
                        round
                        onClick={e => {
                          e.preventDefault();
                          window.open(
                            `${config.API_URL}/solicitacaodigitalfile/${s.id}`,
                            '_blank',
                          );
                        }}
                      >
                        <FiEye />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <NotFound img={NoSolicitacaoChamada} title="Solicitação Digital">
            Nenhuma Solicitação de Digital encontrada
          </NotFound>
        )}
      </Card>
    </Container>
  );
}
